import React, { useState, useEffect, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../axiosConfig";
import TrainerLayout from "./TrainerLayout";
import { TrainerContext } from "../../context/TrainerContext";
import { useToast } from "../commons/toast/ToastContext";
import { FiMail, FiPhone, FiClock, FiChevronDown, FiChevronUp } from "react-icons/fi";
import "../../styles/TrainerHelp.css";

const TrainerHelp = () => {
    const { t, i18n } = useTranslation('help');
    const { trainer } = useContext(TrainerContext);
    const toast = useToast();

    const STATUS_LABELS = {
        SENT: t('ticketStatus.sent'),
        VIEWED: t('ticketStatus.viewed'),
        IN_PROGRESS: t('ticketStatus.inProgress'),
        AWAITING_RESPONSE: t('ticketStatus.awaitingResponse'),
        CLOSED: t('ticketStatus.closed')
    };

    const STATUS_COLORS = {
        SENT: "#6b5bfc",
        VIEWED: "#f5a623",
        IN_PROGRESS: "#4a90e2",
        AWAITING_RESPONSE: "#e67e22",
        CLOSED: "#27ae60"
    };

    // Form state
    const [formData, setFormData] = useState({
        contactPreference: "EMAIL",
        contactEmail: "",
        contactPhone: "",
        description: ""
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Rate limiting state
    const [canSubmit, setCanSubmit] = useState(true);
    const [secondsRemaining, setSecondsRemaining] = useState(0);

    // Tickets state
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(true);
    const [expandedTicketId, setExpandedTicketId] = useState(null);

    // Prefill form with trainer data
    useEffect(() => {
        if (trainer) {
            setFormData(prev => ({
                ...prev,
                contactEmail: trainer.email || "",
                contactPhone: trainer.phone || ""
            }));
        }
    }, [trainer]);

    // Fetch rate limit status
    const fetchRateLimitStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get("/api/v1/support/rate-limit-status", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCanSubmit(response.data.canSubmit);
            setSecondsRemaining(response.data.secondsUntilNextSubmission);
        } catch (err) {
            console.error("Failed to fetch rate limit status:", err);
        }
    }, []);

    // Fetch tickets
    const fetchTickets = useCallback(async () => {
        try {
            setLoadingTickets(true);
            const token = localStorage.getItem("jwt");
            const response = await axios.get("/api/v1/support/tickets", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(response.data);
        } catch (err) {
            console.error("Failed to fetch tickets:", err);
        } finally {
            setLoadingTickets(false);
        }
    }, []);

    // Initial data fetch
    useEffect(() => {
        fetchRateLimitStatus();
        fetchTickets();
    }, [fetchRateLimitStatus, fetchTickets]);

    // Countdown timer for rate limiting
    useEffect(() => {
        if (secondsRemaining > 0) {
            const timer = setInterval(() => {
                setSecondsRemaining(prev => {
                    if (prev <= 1) {
                        setCanSubmit(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [secondsRemaining]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.contactEmail || !formData.contactEmail.trim()) {
            newErrors.contactEmail = t('validation.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
            newErrors.contactEmail = t('validation.emailInvalid');
        }

        if (formData.contactPreference === "PHONE") {
            if (!formData.contactPhone || !formData.contactPhone.trim()) {
                newErrors.contactPhone = t('validation.phoneRequired');
            }
        }

        if (!formData.description || !formData.description.trim()) {
            newErrors.description = t('validation.descriptionRequired');
        } else if (formData.description.length > 5000) {
            newErrors.description = t('validation.descriptionMax');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!canSubmit) {
            toast.warning(t('rateLimited'), t('rateLimit'));
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem("jwt");
            await axios.post("/api/v1/support/ticket", formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(t('messageSent'), t('successTitle'));

            // Reset form
            setFormData(prev => ({
                ...prev,
                description: ""
            }));

            // Refresh data
            await fetchRateLimitStatus();
            await fetchTickets();

        } catch (err) {
            if (err.response?.status === 429) {
                setCanSubmit(false);
                setSecondsRemaining(err.response.data.remainingSeconds || 900);
                toast.error(err.response.data.message || t('tooManyTickets'), t('errorTitle'));
            } else {
                toast.error(err.response?.data?.message || t('sendError'), t('errorTitle'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const currentLocale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(currentLocale, {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const formatRemainingTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        if (minutes > 0) {
            return `${minutes} ${t('timeMin')} ${remainingSeconds} ${t('timeSec')}`;
        }
        return `${remainingSeconds} ${t('timeSec')}`;
    };

    const toggleTicketExpand = (ticketId) => {
        setExpandedTicketId(expandedTicketId === ticketId ? null : ticketId);
    };

    return (
        <TrainerLayout>
            <div className="trainer-help-container">
                <h1 className="trainer-help-title">{t('title')}</h1>

                {/* Contact Information Section */}
                <div className="help-contact-info">
                    <h2>{t('contactSupport')}</h2>
                    <p>{t('contactDescription')}</p>
                    <div className="contact-methods">
                        <div className="contact-method">
                            <FiMail className="contact-icon" />
                            <div>
                                <span className="contact-label">{t('emailLabel')}:</span>
                                <a href="mailto:pomoc@petboard.pl" className="contact-value">pomoc@petboard.pl</a>
                            </div>
                        </div>
                        <div className="contact-method">
                            <FiPhone className="contact-icon" />
                            <div>
                                <span className="contact-label">{t('phoneLabel')}:</span>
                                <a href="tel:+48576839227" className="contact-value">576 839 227</a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Support Form Section */}
                <div className="help-form-section">
                    <h2>{t('sendTicket')}</h2>

                    {!canSubmit && (
                        <div className="rate-limit-warning">
                            <FiClock className="rate-limit-icon" />
                            <span>{t('nextTicketIn')}: <strong>{formatRemainingTime(secondsRemaining)}</strong></span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="help-form">
                        {/* Contact Preference */}
                        <div className="form-group">
                            <label className="form-label">{t('contactPreference')}</label>
                            <div className="radio-group">
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="contactPreference"
                                        value="EMAIL"
                                        checked={formData.contactPreference === "EMAIL"}
                                        onChange={handleChange}
                                    />
                                    <span className="radio-text">{t('contactEmail')}</span>
                                </label>
                                <label className="radio-label">
                                    <input
                                        type="radio"
                                        name="contactPreference"
                                        value="PHONE"
                                        checked={formData.contactPreference === "PHONE"}
                                        onChange={handleChange}
                                    />
                                    <span className="radio-text">{t('contactPhone')}</span>
                                </label>
                            </div>
                        </div>

                        {/* Email */}
                        <div className="form-group">
                            <label className="form-label" htmlFor="contactEmail">
                                {t('emailLabel')} <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                id="contactEmail"
                                name="contactEmail"
                                value={formData.contactEmail}
                                onChange={handleChange}
                                placeholder={t('emailPlaceholder')}
                                className={errors.contactEmail ? "input-error" : ""}
                            />
                            {errors.contactEmail && <span className="error-message">{errors.contactEmail}</span>}
                        </div>

                        {/* Phone */}
                        <div className="form-group">
                            <label className="form-label" htmlFor="contactPhone">
                                {t('phoneLabel')} {formData.contactPreference === "PHONE" && <span className="required">*</span>}
                            </label>
                            <input
                                type="tel"
                                id="contactPhone"
                                name="contactPhone"
                                value={formData.contactPhone}
                                onChange={handleChange}
                                placeholder={t('phonePlaceholder')}
                                className={errors.contactPhone ? "input-error" : ""}
                            />
                            {errors.contactPhone && <span className="error-message">{errors.contactPhone}</span>}
                        </div>

                        {/* Description */}
                        <div className="form-group">
                            <label className="form-label" htmlFor="description">
                                {t('descriptionLabel')} <span className="required">*</span>
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder={t('descriptionPlaceholder')}
                                rows={6}
                                maxLength={5000}
                                className={errors.description ? "input-error" : ""}
                            />
                            <div className="character-count">
                                {formData.description.length} / 5000 {t('characters')}
                            </div>
                            {errors.description && <span className="error-message">{errors.description}</span>}
                        </div>

                        <button
                            type="submit"
                            className="submit-button"
                            disabled={submitting || !canSubmit}
                        >
                            {submitting ? t('sending') : t('sendMessage')}
                        </button>
                    </form>
                </div>

                {/* My Tickets Section */}
                <div className="help-tickets-section">
                    <h2>{t('myTickets')}</h2>

                    {loadingTickets ? (
                        <p className="loading-text">{t('loadingTickets')}</p>
                    ) : tickets.length === 0 ? (
                        <p className="no-tickets-text">{t('noTickets')}</p>
                    ) : (
                        <div className="tickets-list">
                            {tickets.map(ticket => (
                                <div key={ticket.id} className="ticket-card">
                                    <div
                                        className="ticket-header"
                                        onClick={() => toggleTicketExpand(ticket.id)}
                                    >
                                        <div className="ticket-info">
                                            <span className="ticket-date">{formatDate(ticket.createdAt)}</span>
                                            <span
                                                className="ticket-status"
                                                style={{ backgroundColor: STATUS_COLORS[ticket.status] }}
                                            >
                                                {STATUS_LABELS[ticket.status]}
                                            </span>
                                        </div>
                                        <div className="ticket-expand-icon">
                                            {expandedTicketId === ticket.id ? <FiChevronUp /> : <FiChevronDown />}
                                        </div>
                                    </div>
                                    {expandedTicketId === ticket.id && (
                                        <div className="ticket-details">
                                            <div className="ticket-detail-row">
                                                <span className="detail-label">{t('preferredContact')}:</span>
                                                <span className="detail-value">
                                                    {ticket.contactPreference === "EMAIL" ? t('emailLabel') : t('phoneLabel')}
                                                </span>
                                            </div>
                                            <div className="ticket-detail-row">
                                                <span className="detail-label">{t('emailLabel')}:</span>
                                                <span className="detail-value">{ticket.contactEmail}</span>
                                            </div>
                                            {ticket.contactPhone && (
                                                <div className="ticket-detail-row">
                                                    <span className="detail-label">{t('phoneLabel')}:</span>
                                                    <span className="detail-value">{ticket.contactPhone}</span>
                                                </div>
                                            )}
                                            <div className="ticket-description">
                                                <span className="detail-label">{t('descriptionLabel')}:</span>
                                                <p className="description-text">{ticket.description}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </TrainerLayout>
    );
};

export default TrainerHelp;
